import { Canvg } from 'canvg';
import downloadjs from 'downloadjs';
import ExcelJS from 'exceljs';
import html2canvas from 'html2canvas';
import Papa from 'papaparse';
import * as R from 'ramda';
import ReactGA from 'react-ga4';
import { FilterPaneState } from '@blisspointmedia/bpm-types/dist/FilterPane';
import { FilterCategory } from '@blisspointmedia/bpm-types/dist/Performance';
import { Mixpanel, MxE } from './mixpanelWrapper';

export const download = (
  csv: string | File | Blob | Uint8Array,
  fileName: string,
  mimeType?: string | undefined
): void => {
  // Log to GA4 so we can keep track of what's being downloaded from the app.
  ReactGA.event({
    category: 'download',
    action: 'download',
    label: fileName
  });
  Mixpanel.track(MxE.CsvDownload, {
    fileName
  });

  downloadjs(csv, fileName, mimeType);
};

export const downloadJSONToCSV = (
  data: Record<string, any>[],
  fileName: string,
  config: Papa.UnparseConfig = {}
): void => {
  if (!(data && fileName)) {
    console.error('Must provide data and fileName to download JSON to CSV');
    return;
  }
  const csv = Papa.unparse(data, config);
  download(csv, `${fileName}.csv`, 'text/csv');
};

export const convertSVGStringToPNGURI = (
  svgString: string,
  width = 1920,
  height = 1080
): string => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return '';
  }
  const v = Canvg.fromString(ctx, svgString);
  v.resize(width, height, true);
  v.render();

  const pngUri = canvas.toDataURL('image/png');
  canvas.remove();

  return pngUri;
};

/*
 * Export an array of objects to an Excel file.
 */
export const sanitizeSheetName = (name: string): string =>
  (name.length > 31 ? name.slice(0, 31) : name)
    .replace(/\[/g, '(')
    .replace(/\]/g, ')')
    .replace(/[:/]/g, '-')
    .replace(/[\\?*]/g, '_')
    .toLowerCase();

export const addJsonSheet = (
  workbook: ExcelJS.Workbook,
  sheetName: string,
  data: Record<string, unknown>[],
  numFmts?: Record<string, string>
): void => {
  if (data.length === 0) {
    workbook.addWorksheet(sheetName);
    return;
  }
  const columns = Object.keys(data[0]);
  const worksheet = workbook.addWorksheet(sheetName);
  worksheet.columns = columns.map(key => ({
    header: key,
    key,
    ...(numFmts?.[key] && { style: { numFmt: numFmts[key] } })
  }));
  for (const row of data) {
    worksheet.addRow(row);
  }
};

export const writeWorkbook = async (
  workbook: ExcelJS.Workbook,
  fileName: string
): Promise<void> => {
  const buffer = await workbook.xlsx.writeBuffer();
  download(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }),
    fileName,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  );
};

export const exportToExcel = async (
  data: Record<string, any>[],
  name: string,
  numFmts?: Record<string, string>
): Promise<void> => {
  const sheetName = sanitizeSheetName(name);
  const fileName = `${name}.xlsx`;

  const workbook = new ExcelJS.Workbook();
  addJsonSheet(workbook, sheetName, data, numFmts);

  Mixpanel.track(MxE.ExcelDownload, {
    fileName: name
  });

  await writeWorkbook(workbook, fileName);
};

export interface Sheet {
  name: string;
  data: Record<string, any>[];
}
export const exportMultiSheetToExcel = async (data: Sheet[], fileName: string): Promise<void> => {
  const workbook = new ExcelJS.Workbook();
  const sheetNameFreqMap = new Map<string, number>();

  for (const sheet of data) {
    let resolvedTitle = sanitizeSheetName(sheet.name);

    const count = sheetNameFreqMap.get(resolvedTitle);
    if (count !== undefined) {
      const i = count + 1;
      sheetNameFreqMap.set(resolvedTitle, i);
      const suffix = i.toString();
      resolvedTitle =
        resolvedTitle.length + suffix.length <= 31
          ? resolvedTitle.concat(suffix)
          : resolvedTitle.slice(0, 31 - suffix.length).concat(suffix);
    } else {
      sheetNameFreqMap.set(resolvedTitle, 1);
    }

    addJsonSheet(workbook, resolvedTitle, sheet.data);
  }

  // Tracked here as well as in exportToExcel so an Excel export is reported the same way whether it
  // ended up with one sheet or several.
  Mixpanel.track(MxE.ExcelDownload, {
    fileName
  });

  await writeWorkbook(workbook, `${fileName}.xlsx`);
};

/*
 * Download a PNG of an HTML element.
 */
export const downloadPNG = async (
  elementName: string,
  fileName: string | string[],
  label = '',
  classNamesToRemove: string[] = [],
  shouldReplaceHyphens = true,
  extendLegend = false,
  removeBoxShadow = false,
  // Rasterize <img> elements to data URIs before capture. Needed when images
  // live inside an SVG <foreignObject> (e.g. recharts custom tick labels):
  // html2canvas serializes the SVG to an image, and relative/same-origin <img>
  // src values don't load in that sandboxed context, so they come out blank.
  inlineImages = false
): Promise<void> => {
  const ourFileNameString = Array.isArray(fileName) ? fileName.join('_') : fileName;
  const ourFileName = shouldReplaceHyphens
    ? `${ourFileNameString.replace(/[- ]/g, char => (char === '-' ? '.' : ''))}.png`
    : ourFileNameString;
  const contents = document.querySelector<HTMLElement>(elementName);
  if (!contents) {
    return;
  }

  Mixpanel.track(MxE.PngDownload, {
    fileName: ourFileName,
    label: `${label} ${elementName}`
  });

  const options = {
    backgroundColor: null,
    /* eslint-disable no-param-reassign */
    onclone: (_, el) => {
      const rect = contents.getBoundingClientRect();
      el.style.height = `${Math.max(el.scrollHeight, rect.height)}px`;
      el.style.width = `${Math.max(el.scrollWidth, rect.width)}px`;
      el.style.maxHeight = 'none';
      el.style.maxWidth = 'none';
      el.style.overflow = 'visible';
      el.scrollTop = 0;
      el.scrollLeft = 0;
      if (removeBoxShadow) {
        el.style.boxShadow = 'none';
      }

      if (extendLegend) {
        const legend = el.querySelector('.legend');
        if (legend) {
          legend.style.width = `${legend.scrollWidth}px`;
          legend.style.height = `${legend.scrollHeight}px`;
          legend.style.maxHeight = 'none';
          legend.style.maxWidth = 'none';
          legend.style.overflow = 'visible';
          legend.scrollTop = 0;
          legend.scrollLeft = 0;

          let parent = legend.parentElement;
          while (parent && parent !== el) {
            parent.style.height = 'auto';
            parent.style.maxHeight = 'none';
            parent.style.width = 'auto';
            parent.style.maxWidth = 'none';
            parent.style.overflow = 'visible';
            parent = parent.parentElement;
          }
        }
      }
      const chart = el.querySelector('.chartContainer');
      if (chart) {
        chart.style.backgroundColor = 'white';
      } else {
        el.style.backgroundColor = 'white';
      }
      if (inlineImages) {
        // Build a src -> data-URI map from the already-loaded live images, then
        // rewrite every cloned image with a matching src. Keyed by src (not DOM
        // index) so it is robust to ordering and to rows that render an avatar
        // instead of an <img>.
        const dataUriBySrc = new Map<string, string>();
        contents.querySelectorAll('img').forEach(live => {
          if (
            !(live instanceof HTMLImageElement) ||
            !live.complete ||
            live.naturalWidth === 0 ||
            dataUriBySrc.has(live.src)
          ) {
            return;
          }
          try {
            const canvas = document.createElement('canvas');
            canvas.width = live.naturalWidth;
            canvas.height = live.naturalHeight;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;
            ctx.drawImage(live, 0, 0);
            dataUriBySrc.set(live.src, canvas.toDataURL('image/png'));
          } catch {
            // Leave the original src if the image can't be rasterized (e.g. a
            // cross-origin image that would taint the canvas).
          }
        });
        el.querySelectorAll('img').forEach(clonedImg => {
          const uri = dataUriBySrc.get(clonedImg.src);
          if (uri) {
            clonedImg.src = uri;
          }
        });

        // html2canvas serializes SVG <foreignObject> content into a standalone
        // SVG image where the document's class-based CSS does not apply — only
        // inline styles survive. So copy each node's computed style (badge chips,
        // fl/ex layout, beaker color via currentColor) onto inline styles. Read
        // from the live nodes (guaranteed rendered) and map to the clone by index.
        const STYLE_PROPS = [
          'background-color',
          'color',
          'border',
          'border-radius',
          'padding',
          'margin',
          'font-size',
          'font-weight',
          'font-family',
          'line-height',
          'letter-spacing',
          'text-transform',
          'white-space',
          'display',
          'align-items',
          'justify-content',
          'gap',
          'width',
          'height',
          'min-width',
          'box-sizing'
        ];
        const liveFoNodes = contents.querySelectorAll('foreignObject *');
        const cloneFoNodes = el.querySelectorAll('foreignObject *');
        // Only apply styles when counts match — html2canvas can inject wrapper nodes
        // into the clone, making index-based pairing produce wrong results.
        if (liveFoNodes.length === cloneFoNodes.length) {
          cloneFoNodes.forEach((node, i) => {
            const live = liveFoNodes[i];
            if (!live || !(node instanceof Element) || !('style' in node)) return;
            const cs = window.getComputedStyle(live);
            STYLE_PROPS.forEach(prop => {
              const value = cs.getPropertyValue(prop);
              if (value) {
                (node as HTMLElement).style.setProperty(prop, value);
              }
            });
          });
        }
      }
      for (const name of classNamesToRemove) {
        const elementToRemove = el.querySelector(name);
        if (elementToRemove) {
          elementToRemove.remove();
        }
      }
    },
    /* eslint-enable no-param-reassign */
    scale: 1
  };

  const canvas = await html2canvas(contents, options);

  const newCanvas = document.createElement('canvas');
  newCanvas.width = canvas.width;
  newCanvas.height = canvas.height + 30;
  const newCtx = newCanvas.getContext('2d');

  if (newCtx && label.length) {
    newCtx.drawImage(canvas, 0, 0);
    newCtx.font = '16px Graphik';
    newCtx.fillStyle = 'black';
    newCtx.textAlign = 'start';
    newCtx.fillText(label, 15, canvas.height + 20);
    const dataURL = newCanvas.toDataURL('image/png');
    downloadjs(dataURL, ourFileName, 'image/png');
  } else {
    const dataURL = canvas.toDataURL('image/png');
    downloadjs(dataURL, ourFileName, 'image/png');
  }
};

type Filters = Record<
  string,
  {
    values: string;
    isInclusion: boolean;
  }
>;

export interface DataInfo {
  KPI: string;
  Source: string;
  filters: Filters;
}

interface DataInfoParams {
  kpi: string;
  sources: string;
  filterData: FilterCategory[];
  filterState: FilterPaneState;
}

/**
 * Get a summary of the KPI, source, and filters that are currently applied to a page.
 */
export const getDataInfo = ({
  kpi,
  sources,
  filterData,
  filterState
}: DataInfoParams): DataInfo => {
  const selectedMap = R.pathOr({}, ['basic', 'selectedMap'], filterState); // Map of categories to selected items
  const notMap = R.pathOr({}, ['basic', 'notMap'], filterState); // The categories in selectedMap that are exclusions
  const filters: Filters = {};

  for (const item of filterData) {
    const { label, key } = item;

    const selectedValues = selectedMap[key] || {};
    let values: string[] = [];

    if (R.keys(selectedValues).length > 0) {
      values = R.keys(selectedValues).map(k => String(k));
    }

    filters[label] = {
      values: values.length === 0 ? 'All' : values.join(', '),
      isInclusion: !notMap[key]
    };
  }

  return {
    KPI: kpi,
    Source: sources,
    filters
  };
};

export const dataInfoExcelExport = (dataInfo: DataInfo): Record<string, string>[] => {
  const res: Record<string, string>[] = [];

  res.push({ 'Data Info': `KPI = ${dataInfo.KPI}` });
  res.push({ 'Data Info': `Source = ${dataInfo.Source}` });
  Object.entries(dataInfo.filters).forEach(([key, value]) => {
    const { values, isInclusion } = value;
    const text = `${key} = ${isInclusion ? '' : 'Exlude'} ${values}`;
    res.push({ 'Data Info': text });
  });

  return res;
};
